import Cookies from 'js-cookie';
import env from './env';
import { cookieKeys } from './consts/cookies';

const DEFAULT_REDIRECT = '/profile';

const destinations = { ORDER: 'order', CHROME: 'chrome' } as const;

interface SavedAuthRedirect {
  destination?: (typeof destinations)[keyof typeof destinations];
  params?: string;
  pathname?: string;
  genericUrl?: string;
}

const readSavedRedirect = (): SavedAuthRedirect => {
  try {
    return JSON.parse(Cookies.get(cookieKeys.AUTH_REDIRECT) ?? '{}') as SavedAuthRedirect;
  } catch {
    return {};
  }
};

const writeSavedRedirect = (redirect: SavedAuthRedirect) => {
  // session cookie: the redirect only makes sense within the ongoing login attempt
  Cookies.set(cookieKeys.AUTH_REDIRECT, JSON.stringify(redirect), { path: '/', secure: true });
};

/**
 * Saves a "return to the page you tried to visit but was not authenticated" URL.
 */
export const saveGenericRedirectUrl = (path: string) => {
  // bare `/profile` is where login lands anyway, so it just clears any stale URL
  writeSavedRedirect({ ...readSavedRedirect(), genericUrl: path === DEFAULT_REDIRECT ? undefined : path });
};

/**
 * Saves an explicit `?from=order|chrome` destination to return to after login.
 */
export const saveFromParamRedirect = () => {
  // shredding previous destination, 'cause it's Enter re-entry
  const { genericUrl } = readSavedRedirect();

  const params = new URLSearchParams(location.search);

  const fromParam = params.get('from');

  params.delete('from');

  if (fromParam?.startsWith(destinations.ORDER)) {
    writeSavedRedirect({
      genericUrl,
      destination: destinations.ORDER,
      params: params.toString(),
      pathname: fromParam.slice(destinations.ORDER.length), // cuts of `order` from `order?????`
    });
  } else if (fromParam?.startsWith(destinations.CHROME)) {
    writeSavedRedirect({ genericUrl, destination: destinations.CHROME });
  } else {
    writeSavedRedirect({ genericUrl });
  }
};

type AuthRedirectResult =
  | { type: 'order'; url: string }
  | { type: 'chrome'; url: string }
  | { type: 'generic'; path: string }
  | { type: 'none' };

const resolveAuthRedirect = (): AuthRedirectResult => {
  const { destination, params, pathname, genericUrl } = readSavedRedirect();

  Cookies.remove(cookieKeys.AUTH_REDIRECT, { path: '/' });

  if (destination === destinations.ORDER) {
    // leading `/` keeps the host intact, e.g. `from=order@evil.com` would've produced `https://order.clearvpn.com@evil.com`
    const orderPagePath = pathname?.startsWith('/') ? pathname : '/guest-upgrade/';
    const query = params ? `?${params}` : '';

    return { type: 'order', url: `${env.ORDER_PAGE_URL}${orderPagePath}${query}` };
  }

  if (destination === destinations.CHROME) {
    const query = params ? `?${params}` : '';

    return { type: 'chrome', url: `/h/to-chrome${query}` };
  }

  if (genericUrl) {
    // cookie can be planted from a sibling subdomain, so only same-origin targets are allowed
    const target = new URL(genericUrl, window.location.origin);

    if (target.origin === window.location.origin) {
      // full href, as a bare `//evil.com` pathname would be treated as protocol-relative
      return { type: 'generic', path: target.href };
    }
  }

  return { type: 'none' };
};

/**
 * Redirects to the destination saved before login, either an explicit `?from=order|chrome` one or a generic URL.
 * @returns `true` if the redirect has been initiated
 */
export const resolveAndRedirect = (): boolean => {
  const result = resolveAuthRedirect();

  if (result.type === 'order' || result.type === 'chrome') {
    window.location.href = result.url;

    return true;
  }

  if (result.type === 'generic') {
    // full reload, since the target is usually `/profile?...`, which is already mounted and won't re-fetch on navigate
    window.location.replace(result.path);

    return true;
  }

  return false;
};
